import { Controller } from '@hotwired/stimulus';

/**
 * remark-due-shift
 *
 * Le bouton « +1 sem. » des actions rapides de la fiche remarque (revue mobile,
 * 2026-10-10). Il pose (échéance actuelle, sinon aujourd'hui) + 7 jours dans le
 * champ CACHÉ ISO de `Form::DateInputComponent`, puis y émet `change` (bouillonnant).
 * Deux écouteurs le reçoivent, et aucun n'est réécrit ici :
 *   - `cr-inline-field#patchField` (câblé sur le caché) poste l'échéance : le
 *     bouton passe par le MÊME chemin d'écriture que le calendrier ;
 *   - `date-picker#onHiddenChange` resynchronise l'affichage jj/mm/aaaa.
 *
 * Le calcul est fait en dates CIVILES (année, mois, jour), jamais en millisecondes :
 * un passage à l'heure d'été décalerait un `Date` à minuit d'un jour.
 *
 * Targets:
 *   input — le champ caché ISO (`observation[due_on]`)
 *
 * Actions:
 *   addWeek — échéance + 7 jours, puis `change`
 *
 * @example
 * <div data-controller="remark-due-shift">
 *   <input type="hidden" name="observation[due_on]" value="2026-10-10"
 *          data-remark-due-shift-target="input"
 *          data-action="change->cr-inline-field#patchField">
 *   <button type="button" data-action="click->remark-due-shift#addWeek">+1 sem.</button>
 * </div>
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Formats a civil date as ISO `YYYY-MM-DD`.
 *
 * @param {Date} date a local-time Date
 * @returns {string}
 */
function toIso(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Shifts an ISO civil date by `days`, or `today` when the ISO value is blank or
 * malformed. Pure: `today` is injected so tests never read the clock.
 *
 * @param {string} iso the current value of the hidden input ('' when unset)
 * @param {number} days
 * @param {Date} today
 * @returns {string} the shifted ISO date
 */
export function shiftIsoDate(iso, days, today) {
  const match = ISO_DATE.exec(iso || '');
  const base = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(today.getFullYear(), today.getMonth(), today.getDate());
  base.setDate(base.getDate() + days);
  return toIso(base);
}

export default class extends Controller {
  static targets = ['input'];

  addWeek() {
    if (!this.hasInputTarget) return;

    const input = this.inputTarget;
    input.value = shiftIsoDate(input.value, 7, new Date());
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
